'use client';

import { useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  wide?: boolean;
}

export default function Modal({ open, onClose, title, children, wide }: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  const getFocusableElements = useCallback(() => {
    if (!dialogRef.current) return [];
    return Array.from(
      dialogRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), textarea, input:not([disabled]), select, [tabindex]:not([tabindex="-1"])'
      )
    );
  }, []);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }

      if (e.key === 'Tab') {
        const focusable = getFocusableElements();
        if (focusable.length === 0) {
          e.preventDefault();
          return;
        }

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    },
    [onClose, getFocusableElements]
  );

  useEffect(() => {
    if (open) {
      previousFocusRef.current = document.activeElement as HTMLElement;
      document.addEventListener('keydown', handleKeyDown);

      // Focus the dialog on open
      requestAnimationFrame(() => {
        const focusable = getFocusableElements();
        if (focusable.length > 0) {
          focusable[0].focus();
        } else {
          dialogRef.current?.focus();
        }
      });

      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    } else {
      previousFocusRef.current?.focus();
    }
  }, [open, handleKeyDown, getFocusableElements]);

  if (!open) return null;

  const titleId = 'modal-title';

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 animate-in fade-in duration-200"
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={`relative bg-white rounded-[20px] border border-border shadow-lg w-full ${wide ? 'max-w-[56rem] p-0' : 'max-w-lg p-6'} max-h-[85vh] overflow-y-auto focus:outline-none animate-in zoom-in-95 duration-200`}
        onClick={(e) => e.stopPropagation()}
        tabIndex={-1}
      >
        {wide ? (
          <>
            <div className="flex items-center justify-between px-[30px] pt-[28px] pb-4">
              <h2 id={titleId} className="text-[20px] font-bold text-[#0E0420] m-0">
                {title}
              </h2>
              <button
                type="button"
                className="flex items-center justify-center min-w-[44px] min-h-[44px] bg-transparent border-0 rounded text-xl opacity-70 cursor-pointer transition-opacity hover:opacity-100"
                onClick={onClose}
                aria-label="Close"
              >
                &#x2715;
              </button>
            </div>
            <div className="h-[2px] w-full bg-[#DCDBDE]" />
            <div>{children}</div>
          </>
        ) : (
          <>
            <div className="flex items-center justify-between mb-4">
              <h2 id={titleId} className="font-sans text-lg font-semibold text-brand-dark m-0">
                {title}
              </h2>
              <button
                type="button"
                className="absolute top-4 right-4 flex items-center justify-center min-w-[44px] min-h-[44px] bg-transparent border-0 rounded-md text-lg opacity-70 cursor-pointer transition-opacity duration-150 hover:opacity-100"
                onClick={onClose}
                aria-label="Close"
              >
                &#x2715;
              </button>
            </div>
            <div>{children}</div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}
